<template>
  <div class="backdrop" @click.self="$emit('close')">
    <div class="modal">
      <header class="modal-head">
        <h3>{{ isNew ? '새 일정' : (editing ? '일정 수정' : '일정 상세') }}</h3>
        <button class="ghost" @click="$emit('close')">✕</button>
      </header>

      <!-- 상세 보기 -->
      <div v-if="!isNew && !editing" class="detail">
        <div class="detail-title">
          <span class="detail-dot" :style="{ background: detailColor }">{{ detailEmoji }}</span>
          {{ form.title }}
        </div>
        <div class="detail-row">🕒 {{ form.start_at }} ~ {{ form.end_at }}</div>
        <div class="detail-row" v-if="form.location">📍 {{ form.location }}</div>
        <div class="detail-row desc" v-if="form.description">📝 {{ form.description }}</div>
        <div class="actions">
          <button class="danger" @click="remove">삭제</button>
          <button class="primary" @click="editing = true">수정</button>
        </div>
      </div>

      <!-- 생성/수정 폼 -->
      <form v-else class="form" @submit.prevent="save">
        <label>제목 *
          <input v-model="form.title" required placeholder="일정 제목" />
        </label>
        <div class="grid2">
          <label>시작 날짜 *
            <input type="date" v-model="d.startDate" required />
          </label>
          <label>시작 시간
            <input type="time" v-model="d.startTime" :disabled="form.all_day" />
          </label>
        </div>
        <div class="grid2">
          <label>종료 날짜 *
            <input type="date" v-model="d.endDate" required />
          </label>
          <label>종료 시간
            <input type="time" v-model="d.endTime" :disabled="form.all_day" />
          </label>
        </div>
        <label class="checkbox">
          <input type="checkbox" v-model="form.all_day" /> 하루종일
        </label>
        <div class="colorpick">
          <span class="cp-label">태그</span>
          <div class="swatches">
            <button
              type="button"
              class="swatch none"
              :class="{ sel: !form.tag_id }"
              title="태그 없음"
              @click="form.tag_id = null"
            ><span class="sw-auto"></span></button>
            <button
              v-for="t in tags"
              :key="t.id"
              type="button"
              class="swatch tag-swatch"
              :class="{ sel: form.tag_id === t.id }"
              :style="{ '--sw': t.color }"
              :title="t.name"
              @click="form.tag_id = t.id"
            >{{ t.emoji }}</button>
          </div>
          <span v-if="selectedTag" class="tag-chip" :style="{ background: selectedTag.color }">
            <span v-if="selectedTag.emoji">{{ selectedTag.emoji }}</span> {{ selectedTag.name }}
          </span>
          <span v-else class="tag-chip none">태그 없음</span>
          <p v-if="!tags.length" class="cp-hint">설정 &gt; 태그 설정에서 먼저 태그를 만들어보세요.</p>
        </div>
        <label>장소
          <input v-model="form.location" placeholder="장소 (선택)" />
        </label>
        <label>설명
          <textarea v-model="form.description" rows="3" placeholder="설명 (선택)"></textarea>
        </label>
        <p v-if="error" class="error">{{ error }}</p>
        <div class="actions">
          <button type="button" class="ghost" @click="$emit('close')">취소</button>
          <button type="submit" class="primary" :disabled="saving">
            {{ saving ? '저장중…' : '저장' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import api from '../api';
import { splitDateTime, joinDateTime } from '../lib/dateUtils';

export default {
  name: 'EventModal',
  props: {
    // event === null → 새 일정, defaultDate / defaultEndDate 사용
    event: { type: Object, default: null },
    defaultDate: { type: String, default: '' },
    defaultEndDate: { type: String, default: '' },
  },
  emits: ['close', 'saved', 'deleted'],
  data() {
    const ev = this.event;
    const isNew = !ev;
    const endDefault = this.defaultEndDate || this.defaultDate;
    const multiDay = isNew && endDefault && endDefault !== this.defaultDate;
    const start = isNew
      ? { date: this.defaultDate, time: '09:00' }
      : splitDateTime(ev.start_at);
    const end = isNew
      ? { date: endDefault, time: '10:00' }
      : splitDateTime(ev.end_at);
    return {
      isNew,
      editing: isNew,
      saving: false,
      error: '',
      tags: [],
      form: {
        title: ev ? ev.title : '',
        location: ev ? ev.location || '' : '',
        description: ev ? ev.description || '' : '',
        all_day: ev ? !!ev.all_day : multiDay,
        color: ev ? ev.color || null : null,
        tag_id: ev ? ev.tag_id || null : null,
        start_at: ev ? ev.start_at : '',
        end_at: ev ? ev.end_at : '',
      },
      d: {
        startDate: start.date,
        startTime: start.time,
        endDate: end.date,
        endTime: end.time,
      },
    };
  },
  computed: {
    // 태그가 있으면 태그 색/이모지, 없으면 (구버전) 커스텀 색상, 그것도 없으면 기본 강조색
    detailColor() {
      const tag = this.event && this.event.tag;
      return (tag && tag.color) || this.form.color || 'var(--accent)';
    },
    detailEmoji() {
      const tag = this.event && this.event.tag;
      return (tag && tag.emoji) || '';
    },
    // 폼에서 현재 선택된 태그 (글자로 보여주기 위함 — 원형 스와치만으로는 hover 없인 이름이 안 보임)
    selectedTag() {
      return this.tags.find((t) => t.id === this.form.tag_id) || null;
    },
  },
  async created() {
    try { this.tags = (await api.listTags()).tags; } catch (e) { /* noop */ }
  },
  methods: {
    buildPayload() {
      const allDay = this.form.all_day;
      const startTime = allDay ? '00:00' : this.d.startTime;
      const endTime = allDay ? '23:59' : this.d.endTime;
      return {
        title: this.form.title.trim(),
        location: this.form.location || null,
        description: this.form.description || null,
        all_day: allDay,
        tag_id: this.form.tag_id || null,
        start_at: joinDateTime(this.d.startDate, startTime),
        end_at: joinDateTime(this.d.endDate, endTime),
      };
    },
    async save() {
      this.error = '';
      this.saving = true;
      try {
        const payload = this.buildPayload();
        let saved;
        if (this.isNew) {
          saved = (await api.createEvent(payload)).event;
        } else {
          saved = (await api.updateEvent(this.event.id, payload)).event;
        }
        this.$emit('saved', saved);
      } catch (e) {
        this.error = e.message || '저장 실패';
      } finally {
        this.saving = false;
      }
    },
    async remove() {
      if (!confirm('이 일정을 삭제할까요?')) return;
      try {
        await api.deleteEvent(this.event.id);
        this.$emit('deleted', this.event.id);
      } catch (e) {
        this.error = e.message || '삭제 실패';
      }
    },
  },
};
</script>

<style scoped>
.backdrop {
  position: fixed; inset: 0; z-index: 50;
  background: rgba(0, 0, 0, 0.45);
  display: grid; place-items: center; padding: 16px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  width: 100%; max-width: 440px;
  max-height: 92vh; overflow: auto;
}
.modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px; border-bottom: 1px solid var(--border);
}
.modal-head h3 { margin: 0; font-size: 17px; }
.form, .detail { padding: 18px; display: flex; flex-direction: column; gap: 14px; }
label { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: var(--muted); }
.grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.checkbox { flex-direction: row; align-items: center; gap: 8px; color: var(--text); }
.checkbox input { width: auto; }
.actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }
.error { color: var(--danger); font-size: 13px; margin: 0; }

.detail-title { font-size: 20px; font-weight: 800; display: flex; align-items: center; gap: 9px; }
.detail-dot {
  width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
  display: grid; place-items: center; font-size: 13px; line-height: 1;
}
.detail-row { font-size: 14px; color: var(--text); }
.detail-row.desc { white-space: pre-wrap; color: var(--muted); }

/* 색상 선택 — 가로 스크롤 스와치 */
.colorpick { display: flex; flex-direction: column; gap: 8px; }
.cp-label { font-size: 13px; color: var(--muted); }
.swatches {
  display: flex; gap: 10px; overflow-x: auto; padding: 4px 2px 8px;
  scroll-snap-type: x proximity; -webkit-overflow-scrolling: touch;
}
.swatches::-webkit-scrollbar { height: 5px; }
.swatches::-webkit-scrollbar-thumb { background: var(--border); border-radius: 999px; }
.swatch {
  flex: 0 0 auto; width: 28px; height: 28px; padding: 0; border-radius: 50%;
  border: 2px solid transparent; background: var(--sw); cursor: pointer;
  scroll-snap-align: center; transition: transform 0.1s ease, box-shadow 0.12s ease;
  position: relative;
}
.swatch:hover { background: var(--sw); transform: scale(1.08); }
.swatch.sel {
  box-shadow: 0 0 0 2px var(--surface), 0 0 0 4px var(--sw, var(--accent));
}
.swatch.sel::after {
  content: '✓'; position: absolute; inset: 0; display: grid; place-items: center;
  color: #fff; font-size: 14px; font-weight: 900; text-shadow: 0 1px 1px rgba(0,0,0,0.35);
}
.swatch.none { --sw: var(--accent); background: var(--surface-2); border-color: var(--border); }
.swatch.none .sw-auto {
  position: absolute; inset: 3px; border-radius: 50%;
  background: conic-gradient(#6366f1, #06b6d4, #10b981, #f59e0b, #ef4444, #ec4899, #6366f1);
  opacity: 0.85;
}
.swatch.none.sel { box-shadow: 0 0 0 2px var(--surface), 0 0 0 4px var(--accent); }
.swatch.none.sel::after { color: var(--text); text-shadow: none; }

/* 태그 스와치 — 이모지를 표시하므로 체크마크 오버레이 대신 링으로만 선택 표시 */
.tag-swatch { display: grid; place-items: center; font-size: 14px; line-height: 1; }
.tag-swatch.sel::after { content: none; }
.cp-hint { margin: 0; font-size: 12px; color: var(--muted); }

/* 현재 선택된 태그 — 원형 스와치는 이름이 안 보이므로 글자 칩으로 함께 표시 */
.tag-chip {
  align-self: flex-start; display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 11px; border-radius: 999px; font-size: 12px; font-weight: 700;
  color: #fff; text-shadow: 0 1px 1px rgba(0,0,0,0.25);
}
.tag-chip.none { background: var(--surface-2); color: var(--muted); border: 1px solid var(--border); text-shadow: none; }
</style>
